import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Icon,
  Stat,
  Tag,
  useErrorDialog,
} from '@dayon/design-system';
import { formatPesos } from '@dayon/money';
import type { PlayerRow, TransactionRow } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { ResidenceValue } from '../../lib/residence.tsx';
import { TX_COLUMNS } from './columns.tsx';
import { initials, relative, riskBand, stamp } from './format.ts';
import { KYC_LABEL, STATUS_LABEL, TIER_LABEL, kycTone, statusTone, tierTone } from './vocabulary.ts';
import type { PlayerRecord } from './types.ts';

/**
 * The roster's quick look.
 *
 * The row already carries name, ref, brand, tier, KYC, status, agent, risk,
 * balance and last seen, so the drawer paints immediately; the record read adds
 * the phone, the registration date and the three lifetime figures, and the
 * ledger read fills the transaction list.
 */
export function QuickLook({
  row,
  onClose,
  canAdjust,
  canNote,
  canViewLedger,
  role,
  onAddNote,
}: {
  row: PlayerRow;
  onClose: () => void;
  canAdjust: boolean;
  canNote: boolean;
  /**
   * `transactions.view`, which THIS DRAWER NEEDS AND THE ROSTER DOES NOT.
   *
   * A Support Agent holds `players.view` and not this. Without the flag the
   * drawer asked for the ledger anyway, was refused, and raised a dialog
   * saying the player could not be loaded — so opening any player told her
   * something had broken when nothing had. She simply is not entitled to the
   * money, which is a sentence, not a failure.
   *
   * `TransactionsTab` on the profile already took this exact prop for this
   * exact reason. The drawer was the copy that missed it.
   */
  canViewLedger: boolean;
  /** Named in the reason, so it reads as a fact about you rather than a fault. */
  role: string;
  /** Opens the note dialog, which the PAGE owns — <Dialog> unmounts its
      children when closed, and a half-typed note must survive a Cancel. */
  onAddNote: () => void;
}) {
  const { showError } = useErrorDialog();
  const [detail, setDetail] = useState<PlayerRecord | null>(null);
  const [ledger, setLedger] = useState<TransactionRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  /* Which of the two reads failed, tracked separately. One shared catch could
     only say "something failed", so the list below named /api/transactions for a
     failure that was just as likely to have been the record read. */
  const [failed, setFailed] = useState({ record: false, ledger: false });

  useEffect(() => {
    const id = row.id;
    let cancelled = false;
    setLoading(true);
    setDetail(null);
    setLedger(null);
    setFailed({ record: false, ledger: false });

    /* allSettled, not all: these are two independent reads and the drawer says
       something different about each. Failing them together also meant a broken
       ledger read blanked the record the drawer had already been given. */
    /* The ledger read is SKIPPED, not attempted and refused. A 403 an operator
       cannot act on is noise, and `allSettled` would still report it as a
       rejection and open the dialog below. */
    void Promise.allSettled([
      api.get<{ player: PlayerRecord }>(`/api/players/${id}`),
      canViewLedger
        ? api.get<{ items: TransactionRow[] }>(`/api/transactions?playerId=${id}&perPage=4`)
        : Promise.resolve({ items: [] as TransactionRow[] }),
    ]).then(([record, tx]) => {
      if (cancelled) return;

      if (record.status === 'fulfilled') setDetail(record.value.player);
      if (tx.status === 'fulfilled') setLedger(tx.value.items);
      setFailed({ record: record.status === 'rejected', ledger: tx.status === 'rejected' });
      setLoading(false);

      // One dialog, naming what actually failed rather than "something".
      const failures = [
        record.status === 'rejected' ? { what: 'record', reason: record.reason as unknown } : null,
        tx.status === 'rejected' ? { what: 'ledger', reason: tx.reason as unknown } : null,
      ].filter((f) => f !== null);
      if (failures.length === 0) return;

      const first = failures[0]!.reason;
      showError({
        title:
          failures.length === 2
            ? 'Could not load this player'
            : failures[0]!.what === 'record'
              ? 'Could not load this player’s record'
              : 'Could not load this player’s transactions',
        message:
          first instanceof ApiRequestError
            ? first.message
            : 'The read did not complete. Try again shortly.',
        ...(first instanceof ApiRequestError ? { code: first.code } : {}),
      });
    });

    return () => {
      cancelled = true;
    };
  }, [row.id, canViewLedger, showError]);

  return (
    <Drawer
      open
      onClose={onClose}
      title={row.name}
      subtitle={
        <>
          <span className="tabular">{row.ref}</span> · {row.brandName} ·{' '}
          {/* Three outcomes, not two. A failed record read is not a record with
              no phone in it — same distinction the Registered row below draws. */}
          {loading ? (
            <span className="text-subtle">loading phone…</span>
          ) : !detail ? (
            <span className="text-subtle">phone unavailable</span>
          ) : detail.phone ? (
            <span className="tabular">{detail.phone}</span>
          ) : (
            <span className="text-subtle">no phone on file</span>
          )}
        </>
      }
      actions={
        <>
          {/* The design writes players/<id>/activity. The route table registers
              /players/:id with no tab segment and the profile carries its tab in
              the query, so that is the link. */}
          <Link className="btn btn-primary" to={`/players/${row.id}?tab=activity`}>
            Open full profile
            <Icon name="arrow-right" />
          </Link>
          {canNote && (
            /* The one write this drawer makes. An operator taking a call from
               the roster should not have to open the profile to record it. */
            <Button onClick={onAddNote}>
              <Icon name="plus" />
              Add note
            </Button>
          )}
          {canAdjust && (
            <Button disabled aria-describedby="players-drawer-gap">
              Adjust balance
            </Button>
          )}
          {/* Review KYC was here, a link to the profile's KYC tab, while that tab gave a
              verdict on the older submissions. That process closed on 23 October 2026 and the
              tab decides nothing now, so the link went with it. */}
        </>
      }
    >
      <div className="row">
        <span aria-hidden="true" className="avatar lg">
          {initials(row.name)}
        </span>
        <Tag tone={statusTone(row.status)}>{STATUS_LABEL[row.status] ?? row.status}</Tag>
        <Tag tone={tierTone(row.tier)}>{TIER_LABEL[row.tier] ?? row.tier}</Tag>
      </div>

      <div className="stat-grid">
        <Stat
          label="Cash balance"
          value={formatPesos(BigInt(row.cashBalance))}
          delta="cash only — bonus and locked funds are a separate account kind"
        />
        <Stat
          label="Lifetime GGR"
          value={formatPesos(BigInt(row.lifetimeGgr))}
          delta="bets less wins over non-voided rounds, lifetime"
        />
      </div>

      <dl className="stack" style={{ gap: 'var(--space-2)' }}>
        <div className="def-row">
          <dt>KYC</dt>
          <dd>
            <Tag tone={kycTone(row.kycStatus)}>{KYC_LABEL[row.kycStatus] ?? row.kycStatus}</Tag>
          </dd>
        </div>
        <div className="def-row">
          <dt>Tier</dt>
          <dd>{TIER_LABEL[row.tier] ?? row.tier}</dd>
        </div>
        <div className="def-row">
          <dt>Agent</dt>
          <dd>{row.agentRef ?? <span className="text-subtle">unattributed</span>}</dd>
        </div>
        <div className="def-row">
          <dt>Registered</dt>
          <dd>
            {detail ? (
              stamp(detail.registered_at)
            ) : (
              <span className="text-subtle">{loading ? 'loading…' : 'unavailable'}</span>
            )}
          </dd>
        </div>
        <div className="def-row">
          <dt>Last seen</dt>
          <dd>
            {row.lastSeenAt ? relative(row.lastSeenAt) : <span className="text-subtle">never</span>}
          </dd>
        </div>
        <div className="def-row">
          <dt>Residence</dt>
          {/* Off the ROW, not the record read, so it paints immediately with
              everything else the roster already carries. NULL prints "Never
              asked" in words: this account registered before the platform put
              the question on the sign-up form, and nothing has asked since. */}
          <dd>
            <ResidenceValue code={row.residenceCountry} />
          </dd>
        </div>
        <div className="def-row">
          <dt>Risk score</dt>
          {/* The score is stored; the band is this application's threshold, so
              the number is always printed beside the word. */}
          <dd>
            {row.riskScore} · {riskBand(row.riskScore)}
          </dd>
        </div>
        {detail?.email && (
          <div className="def-row">
            <dt>Email</dt>
            <dd>{detail.email}</dd>
          </div>
        )}
      </dl>

      <div className="panel-title">Last transactions</div>
      {/* Three distinct states, because they mean different things: still
          reading, the ledger read failed, and the player genuinely has no
          transactions. The middle one is only claimed when the LEDGER read is
          the one that rejected. */}
      {loading ? (
        <div className="skeleton" style={{ height: '120px' }}>
          &nbsp;
        </div>
      ) : !canViewLedger ? (
        /* NOT an empty table. An empty ledger and a ledger you may not read
           look identical in a list, and only one of them is a fact about the
           player. The same distinction TransactionsTab draws on the profile. */
        <EmptyState icon="lock-key" title="The ledger needs its own capability">
          Reading the financial ledger is <span className="tabular">transactions.view</span>, and
          this roster only requires <span className="tabular">players.view</span> — you are {role},
          so the rows were not fetched rather than fetched and refused. This says nothing about
          whether the player has transactions.
        </EmptyState>
      ) : failed.ledger ? (
        <EmptyState icon="warning-circle" title="Transactions could not be read">
          This player&rsquo;s transactions could not be fetched, so the list is empty because
          nothing came back — not because the player has none. Close and reopen the quick look to
          retry.
        </EmptyState>
      ) : ledger === null ? (
        <EmptyState icon="warning-circle" title="Transactions were not read">
          The player record did not load, so the ledger read was not reported either. Nothing here
          says anything about this player&rsquo;s transactions.
        </EmptyState>
      ) : (
        <DataTable
          caption="This player's four most recent ledger transactions"
          columns={TX_COLUMNS}
          rows={ledger}
          rowKey={(t) => t.id}
          empty={
            <EmptyState icon="receipt" title="No transactions">
              A real zero — this player has no transactions.
            </EmptyState>
          }
        />
      )}
      {/*
        * OFFERED ONLY TO SOMEBODY WHO CAN OPEN IT. The Transactions module
        * refuses anyone without `transactions.view`, so this link handed a
        * Support Agent a button whose only destination is a locked screen —
        * the same defect as the roster's Message button, which sent the same
        * role to an announcements module it could not open.
        *
        * The drawer already knows the answer: `canViewLedger` is why the rows
        * above are absent, and it is the same key this link needs.
        */}
      {canViewLedger && (
        <div className="row" style={{ marginTop: 'var(--space-2)' }}>
          <Link
            className="btn btn-secondary btn-sm"
            to={`/transactions?playerId=${row.id}&playerRef=${encodeURIComponent(row.ref)}`}
          >
            Open the full ledger for {row.ref}
            <Icon name="caret-right" />
          </Link>
        </div>
      )}

      <div className="notice notice-quiet" id="players-drawer-gap">
        <Icon name="warning-circle" />
        <div>
There is no <strong>Rail</strong> column (GCash, BPI InstaPay): what this
          drawer can read carries the kind, status, reference and amount but never the rail name.
          Status is shown in its place rather than a guessed rail.
          {canAdjust && (
            <>
              {' '}
              <strong>Adjust balance</strong> is inert <em>here</em>: adjusting a balance really
              does work, but it raises a request a second approver has to decide, and both the form
              and that checker queue live on <strong>Player funds</strong> — this drawer has
              neither. Notes, the tier, the account controls and the points
              ledger are on the profile behind <strong>Open full profile</strong>.
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}
